import React, { useEffect, useState } from "react";
import { Wallet, Users, TrendingUp, MousePointerClick, Copy, Share2, IndianRupee } from "lucide-react";
import api from "@/lib/api";
import { StatCard, Money, Badge, statusTone } from "@/components/common";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function SellerDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/seller/dashboard").then(({ data }) => setD(data)); }, []);
  if (!d) return null;

  const copyLink = () => { navigator.clipboard.writeText(d.referral_url); toast.success("Referral link copied!"); };
  const shareWA = () => window.open(`https://wa.me/?text=${encodeURIComponent(`Join VIRIKS and start learning! ${d.referral_url}`)}`, "_blank");

  return (
    <div data-testid="seller-dashboard">
      <h1 className="font-heading text-3xl font-bold text-navy mb-8">Seller Dashboard</h1>

      <div className="viriks-navy rounded-2xl p-6 text-white mb-8 relative overflow-hidden">
        <div className="absolute inset-0 hero-noise opacity-30" />
        <p className="text-white/60 text-sm relative">Your Referral Code</p>
        <div className="flex flex-wrap items-center gap-4 mt-2 relative">
          <span className="font-heading text-3xl font-black text-amber tracking-wider" data-testid="referral-code">{d.referral_code}</span>
        </div>
        <div className="flex flex-wrap gap-3 mt-5 relative">
          <div className="flex-1 min-w-[200px] bg-white/10 rounded-lg px-4 py-2 text-sm truncate">{d.referral_url}</div>
          <Button className="viriks-amber text-white font-semibold" onClick={copyLink} data-testid="copy-referral"><Copy size={15} className="mr-1" /> Copy</Button>
          <Button className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={shareWA} data-testid="share-whatsapp"><Share2 size={15} className="mr-1" /> WhatsApp</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
        <StatCard label="Total Commission" value={<Money amount={d.total_commission} />} icon={IndianRupee} testid="stat-total-comm" />
        <StatCard label="Available" value={<Money amount={d.available_commission} />} icon={Wallet} accent="bg-emerald-600" testid="stat-available" />
        <StatCard label="Pending" value={<Money amount={d.pending_commission} />} icon={TrendingUp} accent="bg-amber" />
        <StatCard label="Withdrawn" value={<Money amount={d.withdrawn_commission} />} icon={IndianRupee} accent="bg-navy" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
        <StatCard label="Referred Students" value={d.total_students} icon={Users} accent="bg-crimson" />
        <StatCard label="Link Clicks" value={d.total_clicks} icon={MousePointerClick} />
        <StatCard label="Total Sales" value={d.total_sales} icon={TrendingUp} accent="bg-amber" />
      </div>

      <h2 className="font-heading text-xl font-bold text-navy mb-4">Recent Commission Activity</h2>
      {d.recent_transactions.length === 0 ? (
        <div className="bg-white border border-border rounded-lg p-8 text-center text-muted-foreground">No commissions yet. Share your link to start earning!</div>
      ) : (
        <div className="bg-white border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-muted-foreground"><tr><th className="p-4">Course</th><th className="p-4">Sale</th><th className="p-4">Commission</th><th className="p-4">Status</th></tr></thead>
            <tbody>
              {d.recent_transactions.map((t) => (
                <tr key={t.id} className="border-t border-border">
                  <td className="p-4 font-medium text-navy">{t.course_title}</td>
                  <td className="p-4"><Money amount={t.sale_amount} /></td>
                  <td className="p-4 font-semibold text-emerald-600"><Money amount={t.amount} /> ({t.percent}%)</td>
                  <td className="p-4"><Badge tone={statusTone(t.status)}>{t.status}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
